import type {HasLocalCoordinates} from "./HasLocalCoordinates";
import Schwebetuer from "./Schwebetuer";
import SchwebetuerFluegelO3 from "../object3d/SchwebetuerFluegel";
import Brett from "./Brett";
import Front from "./Front";
import Commons from "oc-commons/Commons";
import {v3} from "oc-threejs/short3";

export default class SchwebetuerFluegel implements HasLocalCoordinates {
    // DO NOT CHANGE
    className = "SchwebetuerFluegel";

    static decor_material_name = 'U323';

    // persistent (UserLayer) ------------------------------------------
    material: ?string = null; // 'spiegel' | null == globales Material der Front
    streifen: ?string = null;//'spiegel'; // 'spiegel' | 'decor' == globales Material des Dekorstreifens | null == kein Dekorstreifen
    drittelHoehe = false;

    // transient (CompiledLayer) ---------------------------------------

    schwebetuer: Schwebetuer;
    fronten: Front[] = [];
    griffleisten: Brett[] = [];
    schlitten: Brett[] = [];
    object3d: ?THREE.Object3D;

    constructor(schwebetuer) {
        this.schwebetuer = schwebetuer;
    }

    get parent() {
        return this.schwebetuer;
    }

    set decorType(type) {
        const setDecor = () => {
            if (this.material=='spiegel') {
                this.material = this.arrangement.materials.corpus;
            }
            if (this.streifen=='spiegel' || this.streifen==null) {
                this.streifen = SchwebetuerFluegel.decor_material_name;
            }
        };
        switch (type) {
            case 'standard': {
                if (this.material=='spiegel') {
                    this.material = this.arrangement.materials.corpus;
                }
                this.streifen = null;
                this.drittelHoehe = false;
                return;
            }
            case 'spiegel': {
                this.material = 'spiegel';
                this.streifen = null;
                this.drittelHoehe = false;
                return;
            }
            case 'decorDrittel': {
                this.drittelHoehe = true;
                setDecor()
                return;
            }
            case 'decor': {
                this.drittelHoehe = false;
                setDecor();
                return;
            }
        }
        throw "unknown type "+type;
    }

    get decorType() {
        return this.material == 'spiegel'
            ? 'spiegel'
            : (this.streifen==null
                ? 'standard'
                : (this.drittelHoehe
                    ? 'decorDrittel'
                    : 'decor'));
    }

    get index() {
        return this.schwebetuer.schwebetuerFluegel.findIndex(f => f === this);
    }

    get element() {
        return this.schwebetuer.schrank.elements[this.index];
    }

    get schrank() {
        return this.schwebetuer.schrank;
    }

    get arrangement() {
        return this.schrank.arrangement;
    }

    get child3dContainer() {
        return this.object3d;
    }

    get schiebetuerLinksVorne() {
        return this.schrank.schiebetuerLinksVorne && this.schwebetuer.schwebetuerFluegel.length==2;
    }

    get schieneIndex() {
        return ((this.schiebetuerLinksVorne ? 1 : 0) + this.index) % 2;
    }

    schrankToLocal(v: THREE.Vector3) {
        return this.schwebetuer.schrankToLocal(v);
    }

    localToSchrank(v: THREE.Vector3) {
        return this.schwebetuer.localToSchrank(v);
    }

    localToSchrankBox(box) {
        return new THREE.Box3(
            this.localToSchrank(box.min),
            this.localToSchrank(box.max)
        );
    }

    fromJSON(json) {
        //super.fromJSON(json);
        this.product = _.get(json, 'product', null);
        this.material = _.get(json, 'material', null);
        this.streifen = _.get(json, 'streifen', null);
        this.drittelHoehe = json.drittelHoehe || false;
        return this;
    }

    toJSON() {
        return {
            className: this.className,
            // product: this.product,
            material: this.material,
            streifen: this.streifen,
            drittelHoehe: this.drittelHoehe,
            size: this.box.getSize().toJSON()
        };
    }

    copy(schwebetuerFluegel,createInterceptor) {
        this.product = schwebetuerFluegel.product;
        this.material = schwebetuerFluegel.material;
        this.streifen = schwebetuerFluegel.streifen;
        return this;
    }

    compile() {
        APP.model.lazyCreateObject3d(this, () => {
            const o3 = new SchwebetuerFluegelO3(this);
            this.schwebetuer.child3dContainer.add(o3);
            return o3;
        });

        if (!this.product) {
            const artikelnummer = this.schrank.options.getValue('schiebetuer_standardtuer', 'SchwebetuerStandard');
            this.product = APP.findProduct(artikelnummer);
        }

        const {box,linkerSchrankAbschluss,rechterSchrankAbschluss} = SchwebetuerFluegel.compileFluegelAussenBox(this,{
            brettstaerke: this.schrank.options.getFloat('brett_staerke'),
            vorsprung_min: this.schrank.options.getMinValue('schiebetuer_vorsprung', 2.5),
            vorsprung_max: this.schrank.options.getMaxValue('schiebetuer_vorsprung', 5),
            versatz: this.schrank.options.getFloat('schiebetuer_versatz', 3),
            ueberstand_unten: this.schrank.options.getFloat('schiebetuer_ueberstand_unten', 4),
        });
        this.box = box;

        this._compileGriffleisten(box,linkerSchrankAbschluss,rechterSchrankAbschluss);
        this._compileSchlitten(box);
        this._compileFronten(box);
    }
    
    static compileFluegelAussenBox(fluegel,cfg) {
        const box = fluegel.element.schrankBox.clone();

        box.min.z = fluegel.element.schrankBox.max.z + cfg.vorsprung_min + fluegel.schieneIndex * cfg.vorsprung_max;
        box.max.z = box.min.z + cfg.brettstaerke;

        box.min.y -= cfg.ueberstand_unten;
        box.max.y = fluegel.schrank.corpusBox.max.y;

        const linkerSchrankAbschluss = fluegel.element.wallLeft && !fluegel.element.wallLeft.isInner;
        const rechterSchrankAbschluss = fluegel.element.wallRight && !fluegel.element.wallRight.isInner;

        if (linkerSchrankAbschluss) {
            box.min.x = fluegel.schrank.corpusBox.min.x;
        } else {
            box.min.x -= cfg.versatz;
        }

        if (rechterSchrankAbschluss) {
            box.max.x = fluegel.schrank.corpusBox.max.x;
        } else {
            box.max.x += cfg.versatz;
        }

        return {box,linkerSchrankAbschluss,rechterSchrankAbschluss};
    }

    _compileFronten(box) {

        this.fronten = Commons.setArrayLength(this.fronten, this.streifen? 3 : 1, ()=>new Front(this));

        this.fronten.forEach(f=>f.box.copy(box));

        this.fronten.forEach((front,index)=>{

            const isDekor = index===1;
            const material = isDekor ? this.streifen : this.material;

            front.mainAxes = isDekor ? 'xy' : 'yx';

            front.type = material === 'spiegel' ? 'spiegel' : 'standard';

            const productname = isDekor
                ? (material==='spiegel' ? 'SchwebetuerDekorSpiegel' : 'SchwebetuerDekorStandard')
                : (material==='spiegel' ? 'SchwebetuerSpiegel' : 'SchwebetuerStandard');

            front.product = APP.findProduct(productname);

            front.material = (material=='spiegel' || material=='standard') ? null : material;
            // front.material = front.fixedMaterialKey;

            // console.log(front);
        });

        if (this.fronten.length === 3) {
            const schiebetuer_dekor_hoehe = this.drittelHoehe
                ? box.getSize().y / 3
                : this.schrank.options.getFloat('schiebetuer_dekor_hoehe', 30);
            const schiebetuer_dekor_position = this.schrank.options.getFloat('schiebetuer_dekor_position', 0);
            let h = (box.getSize().y - schiebetuer_dekor_hoehe) / 2;
            if (schiebetuer_dekor_position > 0) {
                h = schiebetuer_dekor_position + (schiebetuer_dekor_hoehe / 2);
            }
            this.fronten[0].box.max.y = this.fronten[0].box.min.y + h;
            this.fronten[1].box.min.y = this.fronten[0].box.max.y;
            this.fronten[1].box.max.y = this.fronten[1].box.min.y + schiebetuer_dekor_hoehe;
            this.fronten[2].box.min.y = this.fronten[1].box.max.y;
        }

        this.fronten.forEach(f=>f.compile());
    }

    // get materialArtikelnummer() {
    //     return this.schrank.materials[this.product.Dekortyp];
    // }

    get materialArtikelnummer() {
        return this.schwebetuer.product.Artikelnummer;
    }

    _compileGriffleisten(box,linkerSchrankAbschluss,rechterSchrankAbschluss) {
        const schiebetuer_griffleiste_breite = this.schrank.options.getFloat('schiebetuer_griffleiste_breite', 1.5);
        const schiebetuer_griffleiste_vorsprung = this.schrank.options.getFloat('schiebetuer_griffleiste_vorsprung', 0.9);
        const schiebetuer_griffleiste_staerke = this.schrank.options.getFloat('schiebetuer_griffleiste_staerke', 0.3);

        box.min.x += schiebetuer_griffleiste_staerke;
        box.max.x -= schiebetuer_griffleiste_staerke;

        const griffleisten = APP.model.lazyPool(this, 'griffleisten', () => {
            const brett = new Brett(this, 'Schwebetuergriffleiste', 'xz');
            brett.fixedMaterialKey = 'alu';
            return brett;
        });

        const leiste = () => {
            const leiste = griffleisten.next();
            leiste.box.min.copy(box.min);
            leiste.box.max.copy(box.max);
            leiste.box.min.y += 0.1;
            leiste.box.max.y -= 0.1;
            leiste.box.min.z -= schiebetuer_griffleiste_staerke;
            return leiste;
        };

        const reihe = this.schieneIndex;

        const leisteLinks = leiste();
        leisteLinks.box.min.x -= schiebetuer_griffleiste_staerke;
        leisteLinks.box.max.x = leisteLinks.box.min.x + schiebetuer_griffleiste_breite;
        leisteLinks.box.max.z += (reihe === 1 || linkerSchrankAbschluss) ? schiebetuer_griffleiste_vorsprung : schiebetuer_griffleiste_staerke;

        const leisteRechts = leiste();
        leisteRechts.box.max.x += schiebetuer_griffleiste_staerke;
        leisteRechts.box.min.x = leisteRechts.box.max.x - schiebetuer_griffleiste_breite;
        leisteRechts.box.max.z += (reihe === 1 || rechterSchrankAbschluss) ? schiebetuer_griffleiste_vorsprung : schiebetuer_griffleiste_staerke;

        griffleisten.cleanup();

        this.griffleisten.forEach(x => {
            x.boxToVertices();
            x.compile();
        });
    }

    _compileSchlitten(box) {
        const schiebetuer_schlitten_hoehe = this.schrank.options.getFloat('schiebetuer_schlitten_hoehe', 4);
        const schiebetuer_schlitten_breite_versatz = this.schrank.options.getFloat('schiebetuer_schlitten_breite_versatz', 10);
        const schiebetuer_schlitten_tiefe = this.schrank.options.getFloat('schiebetuer_schlitten_tiefe', 6.5);
        const schiebetuer_schlitten_hoehe_versatz = this.schrank.options.getFloat('schiebetuer_schlitten_hoehe_versatz', 1);

        const reihe = this.schieneIndex;

        const schlittenPool = APP.model.lazyPool(this, 'schlitten', () => {
            const brett = new Brett(this, 'Schwebetuerschlitten', 'xz');
            brett.fixedMaterialKey = 'alu';
            return brett;
        });

        if (reihe === 1) {
            const schlitten = schlittenPool.next();
            schlitten.box.copy(box);
            schlitten.box.max.y += schiebetuer_schlitten_hoehe_versatz;
            schlitten.box.min.y = schlitten.box.max.y - schiebetuer_schlitten_hoehe;
            schlitten.box.min.x += schiebetuer_schlitten_breite_versatz;
            schlitten.box.max.x -= schiebetuer_schlitten_breite_versatz;
            schlitten.box.max.z = box.min.z;
            schlitten.box.min.z = box.min.z - schiebetuer_schlitten_tiefe;
        }
        schlittenPool.cleanup();

        this.schlitten.forEach(x => {
            x.boxToVertices();
            x.compile();
        });
    }

    setProduct(product) {
        this.product = product;
        const productData = this.product.Art.split(':');
        this.material = _.get(productData, '0', 'standard');
        this.streifen = _.get(productData, '1', null);
        this.compile();
        APP.model.setDirty();
    }

    // setDoorTypeFromProduct(product) {
    //     if (product.Art === 'spiegel') {
    //         this.streifen = null;
    //         this.material = 'spiegel';
    //     } else {
    //         this.material = null;
    //     }
    //     this.compile();
    //     APP.model.setDirty();
    // }

    // setDecorStreifenTypeFromProduct(product) {
    //     if (this.material !== 'spiegel') {
    //         if (product.Art === 'standard') {
    //             this.streifen = 'decor';
    //         } else if (product.Art === 'spiegel') {
    //             this.streifen = 'spiegel';
    //         } else {
    //             this.streifen = null;
    //         }
    //     }
    //     this.compile();
    //     APP.model.setDirty();
    // }

    iterate(iterator) {
        iterator(this);
        this.fronten.forEach(x=>x.iterate(iterator));
        this.griffleisten.forEach(x=>x.iterate(iterator));
        this.schlitten.forEach(x=>x.iterate(iterator));
    }

}

